import React, { useState } from 'react';
import { getAppToken, getUserInfo, getIdToken, getAccessToken } from '../utils/auth';
import Header from './Header';
import Footer from './Footer';
import Chatbot from './Chatbot';
import './Dashboard.css';
import TestBubblesCSS from './TestBubblesCSS';
import './TestBubbles.css';

const Dashboard = () => {
  const [numResults, setNumResults] = useState(5);
  const [showProjectInput, setShowProjectInput] = useState(false);
  const [projectDescription, setProjectDescription] = useState('');
  const [activeComponent, setActiveComponent] = useState(null);
  const [apiResults, setApiResults] = useState({});
  const [loading, setLoading] = useState({});
  const [showCards, setShowCards] = useState({});

  // WSO2 APIM Gateway - definito prima per essere utilizzabile nelle funzioni
  const API_BASE = 'https://gw.oxy25.profesia.it/oxy25api/1.0'; // APIM Gateway
  const userInfo = getUserInfo();
  
  const participantId = userInfo && userInfo.given_name && userInfo.family_name
    ? `${userInfo.given_name.toLowerCase()}.${userInfo.family_name.toLowerCase()}` // formato nome.cognome per il backend
    : (userInfo && userInfo.sub ? userInfo.sub : null); // Fallback a sub se disponibile
  
  // Debug: mostra cosa contiene userInfo
  console.log('userInfo:', userInfo);
  console.log('participantId:', participantId);

  // Definisco handleButtonClick dopo le variabili
  const handleButtonClick = (button) => {
    setShowCards({});
    setShowProjectInput(false);
    if (button === 'CHATBOT') {
      setActiveComponent('chatbot');
      return;
    }
    const apiMap = {
      B1: ['A1', `${API_BASE}/profile/${participantId}`],
      B2: ['A2', `${API_BASE}/similar_to_me/${participantId}`],
      B3: ['A3', `${API_BASE}/knows_is`],
      B4: ['A4', `${API_BASE}/knows_apim`],
      B5: ['A5', `${API_BASE}/by_project_ranked`]
    };
    const api = apiMap[button];
    if (api) handleApiCall(api[0], api[1]);
    setShowCards({ [apiMap[button][0]]: true });
  };

  React.useEffect(() => {
    const handler = () => handleButtonClick('B1');
    window.addEventListener('dashboard-profile-click', handler);
    return () => window.removeEventListener('dashboard-profile-click', handler);
  }, [handleButtonClick]);
  
  // Se non abbiamo userInfo o participantId valido, mostra errore
  if (!userInfo || !participantId) {
    return (
      <div className="dashboard-container" style={{position: 'relative', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
        <div style={{background: '#fff', padding: '2rem', borderRadius: '12px', boxShadow: '0 4px 24px rgba(0,0,0,0.1)', textAlign: 'center'}}>
          <h2 style={{color: '#ff4757', marginBottom: '1rem'}}>Autenticazione Richiesta</h2>
          <p>Devi effettuare il login per accedere alla Dashboard.</p>
          <button onClick={() => window.location.href = '/'} style={{padding: '0.7rem 1.5rem', background: '#4a7cff', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer'}}>
            Torna al Login
          </button>
        </div>
      </div>
    );
  }
  
  const displayName = (userInfo && userInfo.given_name && userInfo.family_name)
    ? `${userInfo.given_name.toLowerCase()}.${userInfo.family_name.toLowerCase()}`
    : (userInfo && userInfo.sub ? userInfo.sub.substring(0, 8) : participantId);

  const handleApiCall = async (apiName, endpoint) => {
    setApiResults({});
    setActiveComponent(null);
    setLoading(prev => ({ ...prev, [apiName]: true }));
    try {
      // Ottieni access token per autenticazione OAuth2 tramite APIM
      const accessToken = getAccessToken();
      
      if (!accessToken) {
        throw new Error('Access token non disponibile. Effettua il login.');
      }
      
      let options = {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        }
      };
      
      let url = '';
      switch (apiName) {
        case 'A1':
          url = `${API_BASE}/search/profile/${participantId}`;
          options.method = 'GET';
          break;
        case 'A2':
          url = `${API_BASE}/search/similar_to_me/${participantId}`;
          options.method = 'POST';
          options.body = JSON.stringify({ k: 3 });
          break;
        case 'A3':
          url = `${API_BASE}/search/knows_is`;
          options.method = 'POST';
          options.body = JSON.stringify({});
          break;
        case 'A4':
          url = `${API_BASE}/search/knows_apim`;
          options.method = 'POST';
          options.body = JSON.stringify({});
          break;
        case 'A5':
          url = `${API_BASE}/search/by_project_ranked`;
          options.method = 'POST';
          options.body = JSON.stringify({
            participant_id: participantId,
            project_description: projectDescription || 'DevOps e infrastruttura cloud',
            k: numResults,
            candidate_ids: [participantId],
            enforce_domain_filter: true
          });
          break;
        default:
          url = `${API_BASE}/search/profile/${participantId}`;
          options.method = 'GET';
      }
      
      const response = await fetch(url, options);
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }
      const data = await response.json();
      setApiResults({ [apiName]: data });
      setShowCards(prev => ({ ...prev, [apiName]: true }));
    } catch (error) {
      setApiResults({
        [apiName]: { error: `Failed to call ${apiName}: ${error.message}` }
      });
    } finally {
      setLoading(prev => ({ ...prev, [apiName]: false }));
    }
  };

  const apiLabels = {
    A1: 'Profile',
    A2: 'Similar to me',
    A3: 'Knows IS',
    A4: 'Knows APIM',
    A5: 'Search by project ranked'
  };

  function renderValue(key, value) {
    if (value === null || value === undefined) return <span key={key}>N/A</span>;
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return <span key={key}>{value.toString()}</span>;
    }
    if (Array.isArray(value)) {
      if (value.length === 0) return <span key={key}>N/A</span>;
      return (
        <ul key={key} style={{marginLeft:'1.2rem'}}>
          {value.map((v, i) => (
            <li key={i}>{renderValue(i, v)}</li>
          ))}
        </ul>
      );
    }
    if (typeof value === 'object') {
      return (
        <ul key={key} style={{marginLeft:'1.2rem'}}>
          {Object.entries(value).map(([k, v]) => (
            <li key={k}><strong>{formatLabel(k)}:</strong> {renderValue(k, v)}</li>
          ))}
        </ul>
      );
    }
    return <span key={key}>N/A</span>;
  }

  // Funzione per formattare le label dei campi
  function formatLabel(key) {
    if (key === 'current_company') return 'Company';
    if (key === 'current_position') return 'Position';
    if (key === 'languages') return 'Languages';
    if (key === 'knows_is') return 'Knows IS';
    if (key === 'knows_apim') return 'Knows APIM';
    if (key === 'similarity_score') return 'Similarity';
    if (key === 'reason') return 'Reason';
  // Rimuove la parte prima di _ e mette maiuscola
    if (key.includes('_')) return key.split('_').slice(1).join(' ').replace(/\b\w/g, l => l.toUpperCase());
    return key.charAt(0).toUpperCase() + key.slice(1);
  }

  return (
    <div className="dashboard-container" style={{position: 'relative', minHeight: '100vh', overflow: 'hidden', background: 'transparent'}}>
      <TestBubblesCSS hideText={true} />
  <Header displayName={displayName} />
      <main className="dashboard-main" style={{position: 'relative', zIndex: 2}}>
        <div className="actions-container">
          <div className="buttons-container" style={{display: 'flex', justifyContent: 'center', gap: '2rem', flexWrap: 'wrap'}}>
            <button className="action-button b2" onClick={() => handleButtonClick('B2')}>Similar to me</button>
            <button className="action-button b3" onClick={() => handleButtonClick('B3')}>Knows IS</button>
            <button className="action-button b4" onClick={() => handleButtonClick('B4')}>Knows APIM</button>
          </div>
          <div style={{display: 'flex', justifyContent: 'center', marginTop: '1.5rem'}}>
            <button className="action-button b5" style={{minWidth: '320px', fontSize: '1.15rem'}} onClick={() => setShowProjectInput(true)}>Search by project ranked</button>
          </div>
          {showProjectInput && (
            <div className="project-input-box" style={{margin: '2rem auto 0 auto', background: '#fff', borderRadius: '14px', boxShadow: '0 4px 24px rgba(74,124,255,0.12)', padding: '2rem', maxWidth: '540px', width: '100%'}}>
              <label htmlFor="projectDescription" style={{fontWeight: 600, color: '#4a7cff', marginBottom: '0.7rem', display: 'block', fontSize: '1.15rem'}}>Project description:</label>
              <input
                id="projectDescription"
                type="text"
                value={projectDescription}
                onChange={e => setProjectDescription(e.target.value)}
                placeholder="Example: Cybersecurity project to implement new security measures"
                style={{width: '100%', padding: '1rem', borderRadius: '10px', border: '1px solid #e0e6f0', fontSize: '1.08rem', marginBottom: '1.2rem'}}
              />
              <div style={{marginBottom:'1.2rem'}}>
                <label htmlFor="numResults" style={{fontWeight: 500, color: '#4a7cff', marginBottom: '0.4rem', display: 'block', fontSize: '1.08rem'}}>Number of results:</label>
                <select
                  id="numResults"
                  value={numResults}
                  onChange={e => setNumResults(Number(e.target.value))}
                  style={{width:'100%', padding:'0.7rem', borderRadius:'10px', border:'1px solid #e0e6f0', fontSize:'1.08rem'}}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={15}>15</option>
                  <option value={20}>20</option>
                </select>
              </div>
              <div style={{display: 'flex', gap: '1.2rem'}}>
                <button className="action-button b5" onClick={() => { handleButtonClick('B5'); setShowProjectInput(false); }} style={{flex: 1, fontSize: '1.08rem'}}>Search</button>
                <button className="action-button" style={{background: '#eee', color: '#4a7cff', flex: 1, fontSize: '1.08rem'}} onClick={() => setShowProjectInput(false)}>Cancel</button>
              </div>
            </div>
          )}
        </div>
        <div className="results-container">
          <div className="api-results">
            {['A1','A2','A3','A4','A5'].map(apiName => {
              const result = apiResults[apiName];
              const isLoadingApi = loading[apiName];
              if (isLoadingApi && showCards[apiName]) {
                return (
                  <div className="profile-card modern" key={apiName+"_loading"}>
                    <div className="loading-spinner-card">
                      <div className="spinner-small"></div>
                      <span style={{color:'#4a7cff', fontWeight:600, fontSize:'1.1rem'}}>Loading {apiLabels[apiName] || apiName}...</span>
                    </div>
                  </div>
                );
              }
              if (!result || result.error || !showCards[apiName]) return null;
              if (Array.isArray(result)) {
                return (
                  <div className="profile-card modern" key={apiName}>
                    <button className="close-card-btn" onClick={() => setShowCards(prev => ({...prev, [apiName]: false}))} title="Chiudi">×</button>
                    <h2 style={{color:'#4a7cff', marginBottom:'0.7rem'}}>{apiLabels[apiName] || apiName}</h2>
                    <div className="similar-list">
                      {result.map((item, idx) => (
                        <div className="similar-profile mini-card" key={item.id || idx}>
                          {/* Header: nome e username come titolo della mini-card */}
                          {(item.full_name || item.id) && (
                            <div className="mini-card-header" style={{textAlign:'center', margin:'0.7rem 0 0.5rem 0'}}>
                              <h3 style={{color:'#4a7cff', fontWeight:700, fontSize:'1.18rem', margin:'0', background:'none'}}>
                                {item.full_name}
                                {item.id && (
                                  <span className="profile-id-tag" style={{marginLeft:'0.7rem', background:'#eaf2ff', color:'#4a7cff', borderRadius:'8px', padding:'2px 8px', fontSize:'0.98rem', fontWeight:500}}>{item.id}</span>
                                )}
                              </h3>
                            </div>
                          )}
                          <div className="profile-fields">
                            {/* Company, Position, knows_is/knows_apim, summary, altri campi */}
                            {item.current_company && (
                              <div style={{marginTop:'0.5rem'}}><strong>Company:</strong> {item.current_company}</div>
                            )}
                            {item.current_position && (
                              <div style={{marginBottom:'0.5rem'}}><strong>Position:</strong> {item.current_position}</div>
                            )}
                            {(() => {
                              const knowsIs = item.knows_is;
                              const knowsApim = item.knows_apim;
                              if (typeof knowsIs !== 'undefined' || typeof knowsApim !== 'undefined') {
                                return (
                                  <div style={{display:'flex', alignItems:'center', gap:'1.2rem', marginBottom:'0.5rem'}}>
                                    <span style={{display:'flex', alignItems:'center', gap:'0.4rem'}}>
                                      <strong>Knows IS:</strong>
                                      {knowsIs === true ? (
                                        <span style={{color:'green', fontWeight:600, fontSize:'1.2rem'}}>✔</span>
                                      ) : knowsIs === false ? (
                                        <span style={{color:'red', fontWeight:600, fontSize:'1.2rem'}}>✖</span>
                                      ) : null}
                                    </span>
                                    <span style={{display:'flex', alignItems:'center', gap:'0.4rem'}}>
                                      <strong>Knows APIM:</strong>
                                      {knowsApim === true ? (
                                        <span style={{color:'green', fontWeight:600, fontSize:'1.2rem'}}>✔</span>
                                      ) : knowsApim === false ? (
                                        <span style={{color:'red', fontWeight:600, fontSize:'1.2rem'}}>✖</span>
                                      ) : null}
                                    </span>
                                  </div>
                                );
                              }
                              return null;
                            })()}
                            {Object.entries(item).map(([key, value]) => {
                              if (key === 'full_name' || key === 'id' || key === 'knows_is' || key === 'knows_apim' || key === 'current_company' || key === 'current_position') return null;
                              if (key === 'summary') {
                              
                                return (
                                  <div key={key} style={{marginBottom:'0.7rem', color:'#222'}}>{value}</div>
                                );
                              }
                              if (key === 'similarity_score') {
                                const percent = Math.round((value || 0) * 100);
                                return (
                                  <div key={key} className="similar-bar-wrap">
                                    <strong>Similarity:</strong>
                                    <div className="similar-bar-bg">
                                      <div className="similar-bar-fill" style={{width: percent + '%'}}></div>
                                    </div>
                                    <span className="similar-bar-label">{percent}%</span>
                                  </div>
                                );
                              }
                              return (
                                <div key={key}><strong>{formatLabel(key)}:</strong> {renderValue(key, value)}</div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              }
                
              if (apiName === 'A5' && result) {
                
                let profiles = Array.isArray(result.results) ? result.results : [];
                let llm = result.llm_raw;
                return (
                  <div className="profile-card modern" key={apiName+"_results"}>
                    <button className="close-card-btn" onClick={() => setShowCards(prev => ({...prev, [apiName]: false}))} title="Chiudi">×</button>
                    <h2 style={{color:'#4a7cff', marginBottom:'0.7rem'}}>{apiLabels[apiName] || apiName}</h2>
                    <div className="similar-list">
                      {profiles.length > 0 ? profiles.map((item, idx) => (
                        <div className="similar-profile mini-card" key={item.id || idx}>
                          {/* Header: nome e username come titolo della mini-card */}
                          {(item.full_name || item.id) && (
                            <div className="mini-card-header" style={{textAlign:'center', margin:'0.7rem 0 0.5rem 0'}}>
                              <h3 style={{color:'#4a7cff', fontWeight:700, fontSize:'1.18rem', margin:'0', background:'none'}}>
                                {item.full_name}
                                {item.id && (
                                  <span className="profile-id-tag" style={{marginLeft:'0.7rem', background:'#eaf2ff', color:'#4a7cff', borderRadius:'8px', padding:'2px 8px', fontSize:'0.98rem', fontWeight:500}}>{item.id}</span>
                                )}
                              </h3>
                            </div>
                          )}
                          <div className="profile-fields">
                            {/* Company, Position, knows_is/knows_apim, summary, altri campi */}
                            {item.current_company && (
                              <div style={{marginTop:'0.5rem'}}><strong>Company:</strong> {item.current_company}</div>
                            )}
                            {item.current_position && (
                              <div style={{marginBottom:'0.5rem'}}><strong>Position:</strong> {item.current_position}</div>
                            )}
                            {(() => {
                              const knowsIs = item.knows_is;
                              const knowsApim = item.knows_apim;
                              if (typeof knowsIs !== 'undefined' || typeof knowsApim !== 'undefined') {
                                return (
                                  <div style={{display:'flex', alignItems:'center', gap:'1.2rem', marginBottom:'0.5rem'}}>
                                    <span style={{display:'flex', alignItems:'center', gap:'0.4rem'}}>
                                      <strong>Knows IS:</strong>
                                      {knowsIs === true ? (
                                        <span style={{color:'green', fontWeight:600, fontSize:'1.2rem'}}>✔</span>
                                      ) : knowsIs === false ? (
                                        <span style={{color:'red', fontWeight:600, fontSize:'1.2rem'}}>✖</span>
                                      ) : null}
                                    </span>
                                    <span style={{display:'flex', alignItems:'center', gap:'0.4rem'}}>
                                      <strong>Knows APIM:</strong>
                                      {knowsApim === true ? (
                                        <span style={{color:'green', fontWeight:600, fontSize:'1.2rem'}}>✔</span>
                                      ) : knowsApim === false ? (
                                        <span style={{color:'red', fontWeight:600, fontSize:'1.2rem'}}>✖</span>
                                      ) : null}
                                    </span>
                                  </div>
                                );
                              }
                              return null;
                            })()}
                            {Object.entries(item).map(([key, value]) => {
                              if (key === 'full_name' || key === 'id' || key === 'knows_is' || key === 'knows_apim' || key === 'current_company' || key === 'current_position') return null;
                              if (key === 'summary') {
                                // Mostra solo il contenuto del summary senza label
                                return (
                                  <div key={key} style={{marginBottom:'0.7rem', color:'#222'}}>{value}</div>
                                );
                              }
                              if (key === 'similarity_score') {
                                const percent = Math.round((value || 0) * 100);
                                return (
                                  <div key={key} className="similar-bar-wrap">
                                    <strong>Similarity:</strong>
                                    <div className="similar-bar-bg">
                                      <div className="similar-bar-fill" style={{width: percent + '%'}}></div>
                                    </div>
                                    <span className="similar-bar-label">{percent}%</span>
                                  </div>
                                );
                              }
                              return (
                                <div key={key}><strong>{formatLabel(key)}:</strong> {renderValue(key, value)}</div>
                              );
                            })}
                          </div>
                        </div>
                      )) : <div style={{color:'#888'}}>No profiles found.</div>}
                    </div>
                    {/* Sotto-card dedicata per llm_raw */}
                    {llm && (
                      <div className="profile-card llm-subcard" style={{marginTop:'2rem', background:'#f6f8fa', borderRadius:'12px', boxShadow:'0 2px 12px rgba(74,124,255,0.07)', padding:'1.5rem'}}>
                        <h3 style={{color:'#4a7cff', marginBottom:'0.7rem'}}>Analisi progetto (LLM)</h3>
                        {/* Se llm è una stringa, mostra come testo preformattato */}
                        {typeof llm === 'string' ? (
                          <pre style={{background:'#fff', borderRadius:'8px', padding:'1rem', fontSize:'1.05rem', color:'#222', whiteSpace:'pre-wrap'}}>{llm}</pre>
                        ) : (
                          <div className="profile-fields">
                            {llm.project_analysis && (
                              <div style={{marginBottom:'0.7rem'}}>
                                <strong>project_analysis:</strong>
                                <div style={{marginTop:'0.2rem', color:'#222', background:'#fff', borderRadius:'8px', padding:'0.7rem', fontSize:'1.05rem'}}>{llm.project_analysis}</div>
                              </div>
                            )}
                            {Array.isArray(llm.ranked_candidates) && llm.ranked_candidates.length > 0 && (
                              <div className="similar-list" style={{marginTop:'1.2rem'}}>
                                <strong style={{color:'#4a7cff', fontSize:'1.07rem'}}>Ranked candidates:</strong>
                                {llm.ranked_candidates.map((item, idx) => (
                                  <div className="similar-profile mini-card" key={item.id || idx}>
                                      {/* Header: nome e username come titolo della mini-card */}
                                      {(item.full_name || item.id) && (
                                        <div className="mini-card-header" style={{textAlign:'center', margin:'0.7rem 0 0.5rem 0'}}>
                                          <h3 style={{color:'#4a7cff', fontWeight:700, fontSize:'1.18rem', margin:'0', background:'none'}}>
                                            {item.full_name}
                                            {item.id && (
                                              <span className="profile-id-tag" style={{marginLeft:'0.7rem', background:'#eaf2ff', color:'#4a7cff', borderRadius:'8px', padding:'2px 8px', fontSize:'0.98rem', fontWeight:500}}>{item.id}</span>
                                            )}
                                          </h3>
                                        </div>
                                      )}
                                      <div className="profile-fields">
                                        {/* Company, Position, knows_is/knows_apim, summary, altri campi */}
                                        {item.current_company && (
                                          <div style={{marginTop:'0.5rem'}}><strong>Company:</strong> {item.current_company}</div>
                                        )}
                                        {item.current_position && (
                                          <div style={{marginBottom:'0.5rem'}}><strong>Position:</strong> {item.current_position}</div>
                                        )}
                                      {(() => {
                                        const knowsIs = item.knows_is;
                                        const knowsApim = item.knows_apim;
                                        if (typeof knowsIs !== 'undefined' || typeof knowsApim !== 'undefined') {
                                          return (
                                            <div style={{display:'flex', alignItems:'center', gap:'1.2rem', marginBottom:'0.5rem'}}>
                                              <span style={{display:'flex', alignItems:'center', gap:'0.4rem'}}>
                                                <strong>Knows IS:</strong>
                                                {knowsIs === true ? (
                                                  <span style={{color:'green', fontWeight:600, fontSize:'1.2rem'}}>✔</span>
                                                ) : knowsIs === false ? (
                                                  <span style={{color:'red', fontWeight:600, fontSize:'1.2rem'}}>✖</span>
                                                ) : null}
                                              </span>
                                              <span style={{display:'flex', alignItems:'center', gap:'0.4rem'}}>
                                                <strong>Knows APIM:</strong>
                                                {knowsApim === true ? (
                                                  <span style={{color:'green', fontWeight:600, fontSize:'1.2rem'}}>✔</span>
                                                ) : knowsApim === false ? (
                                                  <span style={{color:'red', fontWeight:600, fontSize:'1.2rem'}}>✖</span>
                                                ) : null}
                                              </span>
                                            </div>
                                          );
                                        }
                                        return null;
                                      })()}
                                      {Object.entries(item).map(([key, value]) => {
                                        if (key === 'full_name' || key === 'id' || key === 'knows_is' || key === 'knows_apim' || key === 'current_company' || key === 'current_position') return null;
                                        if (key === 'summary') {
                                          
                                          return (
                                            <div key={key} style={{marginBottom:'0.7rem', color:'#222'}}>{value}</div>
                                          );
                                        }
                                        if (key === 'similarity_score') {
                                          const percent = Math.round((value || 0) * 100);
                                          return (
                                            <div key={key} className="similar-bar-wrap">
                                              <strong>Similarity:</strong>
                                              <div className="similar-bar-bg">
                                                <div className="similar-bar-fill" style={{width: percent + '%'}}></div>
                                              </div>
                                              <span className="similar-bar-label">{percent}%</span>
                                            </div>
                                          );
                                        }
                                        return (
                                          <div key={key}><strong>{formatLabel(key)}:</strong> {renderValue(key, value)}</div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              }
              if (typeof result === 'object') {
                const title = apiLabels[apiName] || apiName;
                return (
                  <div className="profile-card modern" key={apiName}>
                    <button className="close-card-btn" onClick={() => setShowCards(prev => ({...prev, [apiName]: false}))} title="Chiudi">×</button>
                    <h2 style={{color:'#4a7cff', marginBottom:'0.5rem'}}>{title}</h2>
                    <div className="profile-fields">
                      {Object.entries(result).map(([key, value]) => {
                        return (
                          <div key={key}><strong>{key}:</strong> {renderValue(key, value)}</div>
                        );
                      })}
                    </div>
                  </div>
                );
              }
              return null;
            })}
            {['A1','A2','A3','A4','A5'].map(apiName => (
              apiResults[apiName] && apiResults[apiName].error && (
                <div className="api-result" key={apiName+"_err"}>
                  <h4>{apiName} Response:</h4>
                  <pre>{JSON.stringify(apiResults[apiName], null, 2)}</pre>
                </div>
              )
            ))}
            {activeComponent === 'chatbot' && (
              <div className="chatbot-container">
                <Chatbot onClose={() => setActiveComponent(null)} />
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Dashboard;
